<!--
 @description Content panel for the blacklisted-challenges settings modal. Shows
 the user's current challenge blacklist and lets them remove individual entries
 (slice 3). Also provides a browse section with search, pack/category filters,
 and infinite scroll to add new entries (slice 4).

 Resolves blacklisted ids to challenge titles using the shared challenge catalogue
 from `useChallengeStore`. When a challenge document no longer exists for a given
 id (EC-06), the raw id is used as the fallback label — the remove action still
 functions normally.

 The over-cap banner (AC-17) is shown when `challengeBlacklist.length > cap`
 and disappears reactively as the user removes entries (AC-19). The banner
 carries `role="alert"` so assistive technology announces it on appearance.

 The browse list (slice 4) shows every challenge in the catalogue — all packs
 are free, so there is no per-user ownership filter — sorted locale-aware
 case-insensitive.
 Search uses `standardizeString` (NFD + lowercase + trim) across title and
 description in both locales (AC-13). Pack and category filters intersect with
 the active search (AC-15, AC-16). Infinite scroll loads 9 challenges per page
 (matching `fetchChallengeBatch` default); the sentinel element carries
 `data-testid="browse-sentinel"` (DEC-019). The `loadNextPage` method is
 exposed via `defineExpose` for test environments where IntersectionObserver
 is unavailable.

 Add-to-blacklist toggle: optimistic (no optimistic UI flip — we flip only on
 success), retry once on `aborted`, revert on any other failure. Per-id
 in-flight guard prevents concurrent writes (EC-08). Server `cap-exceeded`
 opens `CapReachedDialog` without mutating local state (AC-06).

 @props
   isOpen — Forwarded to the inner Modal primitive to control visibility.
   cap    — Maximum number of blacklisted challenges allowed (30% of the full
            challenge catalogue). Computed and passed by Parameters.vue (DEC-009).

 @design
   Renders its own root container with section structure; consumed inside a
   host `<Modal>` for backdrop and ESC handling (DEC-012).
-->
<template>
  <div class="blacklisted-challenges-modal">
    <h2 class="blacklisted-challenges-modal__header">
      {{ t('challengeBlacklist.modalHeader') }}
    </h2>

    <!-- Over-cap banner (AC-17, AC-19) -->
    <div v-if="isOverCap" class="blacklisted-challenges-modal__over-cap-banner" role="alert">
      {{ t('challengeBlacklist.overCapBanner') }}
    </div>

    <!-- Currently blacklisted section (slice 3) -->
    <section class="blacklisted-challenges-modal__section">
      <h3 class="blacklisted-challenges-modal__section-heading">
        {{ t('challengeBlacklist.currentlyBlacklistedHeading') }}
      </h3>

      <!-- Empty state (AC-09) -->
      <p v-if="challengeBlacklist.length === 0" class="blacklisted-challenges-modal__empty">
        {{ t('challengeBlacklist.currentlyBlacklistedEmpty') }}
      </p>

      <!-- Blacklisted challenge rows (AC-10, EC-06) -->
      <ul v-else class="blacklisted-challenges-modal__list">
        <li v-for="id in challengeBlacklist" :key="id" class="blacklisted-challenges-modal__row">
          <span class="blacklisted-challenges-modal__row-label">
            {{ resolveTitle(id) }}
          </span>
          <button
            class="blacklisted-challenges-modal__remove-btn tertiary"
            :aria-label="t('challengeBlacklist.removeActionFor', { title: resolveTitle(id) })"
            @click="removeFromBlacklist(id)"
          >
            {{ t('challengeBlacklist.removeAction') }}
          </button>
        </li>
      </ul>
    </section>

    <!-- Browse and add challenges section (slice 4) -->
    <section class="blacklisted-challenges-modal__browse-section">
      <h3 class="blacklisted-challenges-modal__browse-heading">
        {{ t('challengeBlacklist.browseHeading') }}
      </h3>

      <!-- Search input (AC-13) -->
      <input
        v-model="searchQuery"
        type="text"
        class="blacklisted-challenges-modal__search"
        :placeholder="t('challengeBlacklist.searchPlaceholder')"
        :aria-label="t('challengeBlacklist.searchPlaceholder')"
      />

      <!-- Filters row -->
      <div class="blacklisted-challenges-modal__filters">
        <!-- Pack filter (AC-15) -->
        <label class="blacklisted-challenges-modal__filter-label">
          {{ t('challengeBlacklist.filterByPack') }}
          <select
            v-model="selectedPack"
            class="blacklisted-challenges-modal__filter-select"
            :aria-label="t('challengeBlacklist.filterByPack')"
          >
            <option value="">{{ t('challengeBlacklist.filterByPack') }}</option>
            <option v-for="pack in availablePacks" :key="pack" :value="pack">
              {{ resolvePackTitle(pack) }}
            </option>
          </select>
        </label>

        <!-- Category filter (AC-16) -->
        <label class="blacklisted-challenges-modal__filter-label">
          {{ t('challengeBlacklist.filterByCategory') }}
          <select
            v-model="selectedCategory"
            class="blacklisted-challenges-modal__filter-select"
            :aria-label="t('challengeBlacklist.filterByCategory')"
          >
            <option value="">{{ t('challengeBlacklist.filterByCategory') }}</option>
            <option v-for="category in availableCategories" :key="category" :value="category">
              {{ category }}
            </option>
          </select>
        </label>
      </div>

      <!-- Empty state (AC-14) — only shown when challenges loaded but no matches -->
      <p
        v-if="hasLoadedChallenges && filteredBrowseChallenges.length === 0"
        class="blacklisted-challenges-modal__browse-empty"
      >
        {{ t('challengeBlacklist.searchEmpty') }}
      </p>

      <!-- Browse challenge rows (AC-22, AC-23, AC-24) -->
      <ul v-else class="blacklisted-challenges-modal__browse-list">
        <li
          v-for="challenge in visibleBrowseChallenges"
          :key="challenge.id"
          class="blacklisted-challenges-modal__browse-row"
          data-browse-row
        >
          <span class="blacklisted-challenges-modal__browse-label">
            {{ browseChallengeTitle(challenge) }}
          </span>

          <!-- Blacklisted state (#11b) — shown when challenge is in blacklist -->
          <span
            v-if="challengeBlacklist.includes(challenge.id)"
            class="blacklisted-challenges-modal__blacklisted-state"
          >
            {{ t('challengeBlacklist.blacklistedState') }}
          </span>

          <!-- Blacklist action (#11a) — shown when challenge is NOT in blacklist.
               aria-disabled (not HTML disabled) when at/over cap so the button
               remains focusable and the click handler can open the cap-reached
               dialog via keyboard as well as pointer (AC-12, AC-18, WCAG 2.1.1,
               DEC-032). The handler checks localBlacklist.length >= cap and
               opens capReachedDialogOpen instead of calling the API. -->
          <button
            v-else
            class="blacklisted-challenges-modal__blacklist-btn primary"
            :aria-disabled="localBlacklist.length >= props.cap ? 'true' : 'false'"
            :aria-label="
              t('challengeBlacklist.blacklistActionFor', { title: resolveTitle(challenge.id) })
            "
            @click="addToBlacklist(challenge.id)"
          >
            {{ t('challengeBlacklist.blacklistAction') }}
          </button>
        </li>
      </ul>

      <!-- Infinite scroll sentinel (DEC-019).
           The sentinel is observed by IntersectionObserver in production.
           In test environments (jsdom, no IO), the test may dispatch a
           custom 'intersect' event on this element, or call vm.loadNextPage()
           directly via defineExpose. Both surfaces are valid.
      -->
      <div
        v-if="hasMorePages"
        ref="browseSentinelEl"
        class="blacklisted-challenges-modal__browse-sentinel"
        data-testid="browse-sentinel"
        @intersect="loadNextPage"
      />
    </section>

    <!-- Cap-reached dialog (AC-12, AC-18, AC-06).
         capReachedDialogOpen is a plain ref(false) opened explicitly:
         (1) In addToBlacklist: when localBlacklist.length >= cap (user tap).
         (2) In attemptAdd error path: when server rejects with cap-exceeded.
         Closed by the @close handler setting it back to false (DEC-030).
    -->
    <CapReachedDialog :isOpen="capReachedDialogOpen" @close="capReachedDialogOpen = false" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/authStore'
import { useChallengeStore } from '@/stores/challengesStore'
import { getPublishedPacks } from '@/stores/publishedPacks'
import { updateChallengeBlacklist } from '@/stores/users'
import { standardizeString } from '@/stores/methods'
import type { Pack } from '@/types/Types'
import CapReachedDialog from './CapReachedDialog.vue'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BrowseChallenge {
  id: string
  title: string
  titleEnglish: string
  description: string
  descriptionEnglish: string
  category: string
  packs: string[]
}

// ---------------------------------------------------------------------------
// Props / emits
// ---------------------------------------------------------------------------

interface BlacklistedChallengesModalProps {
  /**
   * Controls visibility. Accepted so Parameters.vue can bind it alongside the
   * outer Modal primitive that wraps this component.
   */
  isOpen?: boolean
  /**
   * Maximum number of challenges the user may blacklist (30% of the full
   * challenge catalogue). Computed and passed by Parameters.vue (DEC-009).
   */
  cap: number
}

const props = defineProps<BlacklistedChallengesModalProps>()

// No emits — the outer Modal primitive's cancel event drives closeModal in
// Parameters.vue, which resets blacklistModalIsOpen.

// ---------------------------------------------------------------------------
// Stores & composables
// ---------------------------------------------------------------------------

const { t, locale } = useI18n()
const authStore = useAuthStore()
const challengeStore = useChallengeStore()

// ---------------------------------------------------------------------------
// Slice 3 — Currently blacklisted state
// ---------------------------------------------------------------------------

/**
 * Local reactive copy of the challenge blacklist. Seeded from the auth store
 * on mount and updated after each remove/add action. Using a local ref (rather
 * than a computed over the store) ensures the component re-renders after
 * mutations regardless of whether the underlying store object is reactive
 * (Pinia in production) or a plain mock object (unit tests).
 *
 * In production, the store's `dbUser.challengeBlacklist` IS reactive (Pinia),
 * so the watcher keeps them in sync. In tests, the watcher is inert (plain
 * object) and `removeFromBlacklist` / `addToBlacklist` drive updates through
 * `localBlacklist`.
 */
const localBlacklist = ref<string[]>(authStore.dbUser?.challengeBlacklist ?? [])

// Keep localBlacklist in sync when the Pinia store updates reactively
// (e.g. after an external mutation or store hydration in production).
watch(
  () => authStore.dbUser?.challengeBlacklist,
  (newVal) => {
    if (newVal !== undefined) {
      localBlacklist.value = [...newVal]
    }
  },
  { deep: true }
)

const challengeBlacklist = computed<string[]>(() => localBlacklist.value)

/** True when the blacklist length exceeds the cap — drives the banner (AC-17). */
const isOverCap = computed(() => challengeBlacklist.value.length > props.cap)

// ---------------------------------------------------------------------------
// Helpers — title resolution
// ---------------------------------------------------------------------------

/**
 * Resolves a blacklisted id to the challenge's locale-appropriate title.
 * Falls back to the raw id when no challenge document is found (EC-06).
 */
function resolveTitle(id: string): string {
  const challenge = challengeStore.challenges.find((c) => c.id === id)
  if (!challenge) return id
  return locale.value === 'fr' ? challenge.title : challenge.titleEnglish
}

/**
 * Returns the locale-appropriate display title for a browse challenge.
 */
function browseChallengeTitle(challenge: BrowseChallenge): string {
  return locale.value === 'fr' ? challenge.title : challenge.titleEnglish
}

/**
 * Published packs, loaded on mount, used to resolve a pack id to its
 * locale-appropriate display title in the pack filter (the challenge `packs`
 * arrays only carry ids). Falls back to the raw id when no pack is found.
 */
const packs = ref<Pack[]>([])

/**
 * Resolves a pack id to the pack's locale-appropriate title. Falls back to the
 * raw id when the pack is not in the published list (matching the title
 * fallback used for blacklisted challenges, EC-06).
 */
function resolvePackTitle(id: string): string {
  const pack = packs.value.find((p) => p.id === id)
  if (!pack) return id
  return locale.value === 'en'
    ? `${pack.title.en} - ${pack.difficulty.en}`
    : `${pack.title.fr} - ${pack.difficulty.fr}`
}

// ---------------------------------------------------------------------------
// Slice 3 — Remove action
// ---------------------------------------------------------------------------

/**
 * Removes a single id from the challenge blacklist. Calls the Cloud Function
 * helper, updates `localBlacklist` so the row disappears immediately (AC-10),
 * and then refreshes `authStore.dbUser` via `authStore.updateDbUser` so that
 * the persisted source-of-truth reflects the new state (REV-01, DEC-017).
 */
async function removeFromBlacklist(id: string): Promise<void> {
  const current = authStore.dbUser?.challengeBlacklist ?? localBlacklist.value
  const updated = current.filter((item) => item !== id)

  try {
    await updateChallengeBlacklist('', updated)
    localBlacklist.value = updated
    authStore.updateDbUser(authStore.$state)
  } catch {
    // Silent catch — remove failure leaves the row in place (project pattern).
  }
}

// ---------------------------------------------------------------------------
// Slice 4 — Cap-reached dialog
// ---------------------------------------------------------------------------

/**
 * Controls cap-reached dialog visibility. Replaced from computed to ref(false)
 * (DEC-030) so the dialog never opens on render — it opens only on an explicit
 * user tap (AC-12, AC-18) or a server cap-exceeded rejection (AC-06).
 *
 * Opened by:
 *   1. addToBlacklist(): localBlacklist.length >= cap → set true, return early.
 *   2. attemptAdd() error path: server rejects with cap-exceeded → set true.
 * Closed by: the @close handler on CapReachedDialog → set false.
 */
const capReachedDialogOpen = ref(false)

// ---------------------------------------------------------------------------
// Slice 4 — In-flight guard (EC-08)
// ---------------------------------------------------------------------------

/** Set of challenge ids currently being added. Prevents double-writes. */
const inFlightIds = ref(new Set<string>())

// ---------------------------------------------------------------------------
// Slice 4 — Browse list: available challenges
// ---------------------------------------------------------------------------

/**
 * Every challenge in the catalogue, sorted locale-aware, case-insensitive on
 * the current-locale title (AC-23). All packs are free, so there is no per-user
 * ownership filter — the browse list shows the full catalogue.
 */
const availableChallenges = computed<BrowseChallenge[]>(() => {
  const rawChallenges = challengeStore.challenges as unknown as BrowseChallenge[]

  return [...rawChallenges].sort((a, b) => {
    const titleA = locale.value === 'fr' ? a.title : a.titleEnglish
    const titleB = locale.value === 'fr' ? b.title : b.titleEnglish
    return titleA.localeCompare(titleB, locale.value, { sensitivity: 'base' })
  })
})

/** True once the challenge catalogue has been loaded (non-empty). */
const hasLoadedChallenges = computed(() => challengeStore.challenges.length > 0)

// ---------------------------------------------------------------------------
// Slice 4 — Search & filter state
// ---------------------------------------------------------------------------

const searchQuery = ref('')
const selectedPack = ref('')
const selectedCategory = ref('')

/** Unique pack ids across available challenges (for the pack filter dropdown). */
const availablePacks = computed<string[]>(() => {
  const packs = new Set<string>()
  for (const c of availableChallenges.value) {
    for (const p of c.packs) packs.add(p)
  }
  return [...packs].sort()
})

/** Unique category names across available challenges (for the category filter dropdown). */
const availableCategories = computed<string[]>(() => {
  const cats = new Set<string>()
  for (const c of availableChallenges.value) {
    if (c.category) cats.add(c.category)
  }
  return [...cats].sort()
})

/**
 * Filtered browse list applying search (AC-13, EC-07), pack filter (AC-15),
 * and category filter (AC-16) as intersecting predicates.
 */
const filteredBrowseChallenges = computed<BrowseChallenge[]>(() => {
  const query = standardizeString(searchQuery.value)
  const pack = selectedPack.value
  const category = selectedCategory.value

  return availableChallenges.value.filter((c) => {
    // EC-07: whitespace-only query is treated as empty (standardizeString trims)
    const matchesSearch =
      query === '' ||
      standardizeString(c.title).includes(query) ||
      standardizeString(c.titleEnglish).includes(query) ||
      standardizeString(c.description).includes(query) ||
      standardizeString(c.descriptionEnglish).includes(query)

    const matchesPack = pack === '' || c.packs.includes(pack)
    const matchesCategory = category === '' || c.category === category

    return matchesSearch && matchesPack && matchesCategory
  })
})

// ---------------------------------------------------------------------------
// Slice 4 — Infinite scroll (DEC-019)
// ---------------------------------------------------------------------------

/** Number of items per page — matches `fetchChallengeBatch` default of 9. */
const PAGE_SIZE = 9

/** How many items are currently visible (grows on each loadNextPage call). */
const visibleCount = ref(PAGE_SIZE)

/** The currently visible slice of filtered challenges (sorted, paginated). */
const visibleBrowseChallenges = computed<BrowseChallenge[]>(() =>
  filteredBrowseChallenges.value.slice(0, visibleCount.value)
)

/** True when there are more challenges to load beyond the current page. */
const hasMorePages = computed(() => visibleCount.value < filteredBrowseChallenges.value.length)

/** Loads the next page of results. Called by IntersectionObserver or tests. */
function loadNextPage(): void {
  visibleCount.value = Math.min(
    visibleCount.value + PAGE_SIZE,
    filteredBrowseChallenges.value.length
  )
}

// Reset pagination when filters/search change
watch([searchQuery, selectedPack, selectedCategory], () => {
  visibleCount.value = PAGE_SIZE
})

// Sentinel element for IntersectionObserver
const browseSentinelEl = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null

onMounted(async () => {
  // Load published packs so the pack filter can show localized pack titles
  // instead of raw ids. getPublishedPacks swallows errors to [] internally, so
  // a load failure simply leaves resolvePackTitle on its id fallback.
  packs.value = await getPublishedPacks()

  if (typeof IntersectionObserver === 'undefined') return
  /* v8 ignore next 12 */ // WHY: IntersectionObserver is unavailable in jsdom; exercised in E2E only
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        loadNextPage()
      }
    },
    { threshold: 0.1 }
  )
  if (browseSentinelEl.value) {
    observer.observe(browseSentinelEl.value)
  }
})

onBeforeUnmount(() => {
  /* v8 ignore next 2 */ // WHY: IntersectionObserver is unavailable in jsdom; exercised in E2E only
  observer?.disconnect()
  observer = null
})

// ---------------------------------------------------------------------------
// Slice 4 — Add-to-blacklist action
// ---------------------------------------------------------------------------

/**
 * Adds a challenge id to the blacklist. Behaviour:
 *
 * - AC-12: if `localBlacklist.length >= cap`, do not call; open cap dialog.
 * - EC-08: if id is already in-flight, ignore the tap.
 * - Optimistic UI: flip localBlacklist immediately; revert on failure.
 * - AC-06: server rejects with cap-exceeded → open cap dialog; revert.
 * - INTERACTION-03: server rejects with unknown-challenge → revert silently.
 * - INTERACTION-05: server rejects with aborted → retry once; revert if retry fails.
 * - DEC-017 pattern: call `authStore.updateDbUser` on success.
 */
async function addToBlacklist(id: string): Promise<void> {
  // AC-12 / AC-18: at or over cap → open cap-reached dialog on tap; no API call.
  // Uses aria-disabled so the button is focusable and this handler fires from
  // both pointer and keyboard (DEC-030, DEC-032).
  if (localBlacklist.value.length >= props.cap) {
    capReachedDialogOpen.value = true
    return
  }

  // EC-08: in-flight guard — ignore double-taps for the same id
  if (inFlightIds.value.has(id)) return

  inFlightIds.value.add(id)

  const previousBlacklist = [...localBlacklist.value]
  const updated = [...previousBlacklist, id]

  // Optimistic UI flip
  localBlacklist.value = updated

  try {
    await attemptAdd(id, updated, previousBlacklist)
  } finally {
    inFlightIds.value.delete(id)
  }
}

/**
 * Internal: performs the callable call with a single retry on `aborted`.
 * Reverts `localBlacklist` on any unrecoverable failure.
 */
async function attemptAdd(
  id: string,
  updated: string[],
  previousBlacklist: string[]
): Promise<void> {
  try {
    await callUpdateBlacklist(id, updated, previousBlacklist, false)
  } catch (err) {
    const code = (err as { code?: string })?.code ?? (err as Error)?.message
    if (code === 'aborted') {
      // INTERACTION-05: retry once
      try {
        await callUpdateBlacklist(id, updated, previousBlacklist, true)
      } catch {
        // Retry failed — revert
        localBlacklist.value = previousBlacklist
      }
    } else {
      // All other errors: revert
      localBlacklist.value = previousBlacklist
      if (code === 'cap-exceeded') {
        // AC-06: open cap-reached dialog via the shared ref (DEC-030)
        capReachedDialogOpen.value = true
      }
      // INTERACTION-03 (unknown-challenge): revert already done, no toast
    }
  }
}

/**
 * Internal: calls `updateChallengeBlacklist` and handles a successful response.
 * Throws on failure so the caller can handle retry/revert logic.
 */
async function callUpdateBlacklist(
  _id: string,
  updated: string[],
  previousBlacklist: string[],
  isRetry: boolean
): Promise<void> {
  try {
    await updateChallengeBlacklist('', updated)
    // Success — localBlacklist was already set optimistically; confirm it.
    // Refresh the auth store (DEC-017 pattern).
    authStore.updateDbUser(authStore.$state)
  } catch (err) {
    const code = (err as { code?: string })?.code ?? (err as Error)?.message
    if (!isRetry && code === 'aborted') {
      // Signal to caller that a retry is warranted
      throw err
    }
    // Non-aborted failure or retry failure: revert + propagate
    localBlacklist.value = previousBlacklist
    throw err
  }
}

// ---------------------------------------------------------------------------
// Expose for testing (DEC-019)
// ---------------------------------------------------------------------------

defineExpose({ loadNextPage })
</script>

<style lang="scss">
.blacklisted-challenges-modal {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;

  &__header {
    margin: 0;
  }

  &__over-cap-banner {
    background-color: var(--color-warning, #fff3cd);
    border: 1px solid var(--color-warning-border, #ffc107);
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    font-weight: 500;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__section-heading,
  &__browse-heading {
    margin: 0;
  }

  &__empty {
    margin: 0;
    color: var(--color-text-secondary, #6c757d);
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--color-border, #dee2e6);
  }

  &__row-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__remove-btn {
    flex-shrink: 0;
  }

  // Browse section (slice 4)

  &__browse-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__search {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--color-border, #dee2e6);
    border-radius: 0.375rem;
  }

  &__filters {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  &__filter-label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
    min-width: 140px;
  }

  &__filter-select {
    padding: 0.375rem 0.5rem;
    border: 1px solid var(--color-border, #dee2e6);
    border-radius: 0.375rem;
  }

  &__browse-empty {
    margin: 0;
    color: var(--color-text-secondary, #6c757d);
  }

  &__browse-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__browse-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--color-border, #dee2e6);
  }

  &__browse-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__blacklisted-state {
    flex-shrink: 0;
    color: var(--color-text-secondary, #6c757d);
    font-style: italic;
  }

  &__blacklist-btn {
    flex-shrink: 0;
  }

  &__browse-sentinel {
    height: 1px;
  }
}
</style>
